import { Product } from '@/lib/types/bevmaq.types';
import { selectItem } from '@/lib/utilities/gtm';
import { SearchIcon } from '@heroicons/react/outline';
import classNames from 'classnames';
import Trans from 'next-translate/Trans';
import useTranslation from 'next-translate/useTranslation';
import Image from 'next/image';
import React from 'react';
import { Pagination } from '../Form/ProductsFilter/Pagination';
import LinkTranslated from '../Translator/LinkTranslated';
import PriceFormat from './Price';

type ProductListProps = {
  products: Product[];
  disablePagination?: boolean;
  isLoading?: boolean;
};

export function ProductListItem({ p }: { p: Product }) {
  const { t } = useTranslation();
  return (
    <LinkTranslated
      href={{
        pathname: '/buy/[slug]',
        query: {
          slug: p.slug,
        },
      }}
      onClick={() => {
        selectItem(p);
      }}
    >
      <div className='group h-full cursor-pointer rounded-xl shadow-xl'>
        <div className='aspect-h-4 aspect-w-8  w-full overflow-hidden rounded-t-lg pb-0 group-hover:opacity-75'>
          <div className='w-66 relative h-52 '>
            <Image
              src={p.thumbnail_image}
              alt='Image of the product'
              fill
              className={classNames({
                'object-cover blur-sm brightness-75': !p.available,
              })}
            />
            {!p.available && (
              <h4 className='absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 -rotate-45 text-4xl font-bold uppercase text-white'>
                {t('common:Sold')}
              </h4>
            )}
          </div>
        </div>

        <div className='px-4 py-2'>
          <h3 className=' mt-4 text-gray-700'>
            {p.model.manufacturer.name} - {p.year} -{' '}
            <span data-testid='product-list-country'>{p.country.name}</span>
          </h3>
          <h4 className='text-sm'>{p.type.name}</h4>
          {p.available && (
            <p className='mt-auto text-lg font-medium text-gray-900'>
              <PriceFormat price={p.price} />
            </p>
          )}
        </div>
      </div>
    </LinkTranslated>
  );
}

function ProductListItemLoading() {
  return (
    <div className='group cursor-pointer rounded-xl shadow-xl'>
      <div className='aspect-h-4 aspect-w-8  w-full overflow-hidden rounded-t-lg pb-0 group-hover:opacity-75'>
        <div className='w-66 relative h-52 animate-pulse bg-slate-200' />
      </div>
      <div className='px-4 py-2'>
        <div className='mt-4 h-4 w-full animate-pulse bg-slate-200 text-sm text-gray-700 ' />
        <div className='mt-1 h-4 w-full animate-pulse bg-slate-200 text-lg font-medium text-gray-900' />
      </div>
    </div>
  );
}

function NoItemsFound() {
  const { t } = useTranslation();
  return (
    <div className='mt-2 flex w-full flex-col items-center justify-center space-x-8 space-y-4 bg-slate-200 p-8 md:flex-row md:space-y-0'>
      <SearchIcon className='h-12' />
      <div className=''>
        <h3 className='text-center text-2xl font-light md:text-left'>
          <Trans
            i18nKey='common:send-us-your-wanted-request'
            components={[<span className='font-semibold' key={1} />]}
          />
        </h3>
        <h3 className='text-center md:text-left'>
          {t('common:if-you-cant-find')}
        </h3>
      </div>
      {/* TODO: Create a special form for the wanted machine */}
      <LinkTranslated href='/contact'>
        <button className='rounded bg-orange-400 px-8 py-4 font-bold text-white hover:bg-orange-500'>
          {t('common:Contact')}
        </button>
      </LinkTranslated>
    </div>
  );
}

function ProductList({
  products,
  disablePagination,
  isLoading,
}: ProductListProps) {
  return (
    <div className='bg-white'>
      <div
        className='mx-auto max-w-2xl px-4 py-8 sm:px-6 lg:max-w-7xl lg:px-8'
        id='products-heading'
      >
        <h2 className='sr-only'>Products</h2>
        <div
          className='grid grid-cols-1 gap-x-6 gap-y-10 sm:grid-cols-2 lg:grid-cols-3 xl:gap-x-8'
          data-testid='product-list'
        >
          {!isLoading
            ? products.map((p) => (
                <ProductListItem key={p.id + Math.random().toString()} p={p} />
              ))
            : Array.from(Array(10).keys()).map((e) => (
                <ProductListItemLoading key={e} />
              ))}
        </div>
        {!isLoading && products.length == 0 ? (
          <NoItemsFound />
        ) : (
          !disablePagination && <Pagination />
        )}
      </div>
    </div>
  );
}

export default ProductList;
